// components/system/ProgressIndicator.jsx
import React from 'react';

// Component constants
const PROGRESS_THRESHOLDS = {
    LOW: 30,     // Below 30% - blue
    MEDIUM: 70   // 30-70% - darker blue, 70%+ - green
};

const TIME_CONSTANTS = {
    SECOND_MS: 1000,
    MINUTE_MS: 60000,
    HOUR_MS: 3600000
};

const ProgressIndicator = ({ 
    progress = 0, 
    isActive = false, 
    currentPhase = null, 
    details = null,
    startTime = null
}) => {
    const getProgressColor = () => {
        if (!isActive) return 'bg-gray-200';
        if (progress < PROGRESS_THRESHOLDS.LOW) return 'bg-blue-500';
        if (progress < PROGRESS_THRESHOLDS.MEDIUM) return 'bg-blue-600';
        return 'bg-green-500';
    };

    const formatElapsedTime = () => {
        if (!startTime) return '';
        const elapsed = Date.now() - new Date(startTime).getTime();
        if (elapsed < TIME_CONSTANTS.SECOND_MS) return `${elapsed}ms`;
        if (elapsed < TIME_CONSTANTS.MINUTE_MS) return `${(elapsed / TIME_CONSTANTS.SECOND_MS).toFixed(1)}s`;
        if (elapsed < TIME_CONSTANTS.HOUR_MS) return `${(elapsed / TIME_CONSTANTS.MINUTE_MS).toFixed(1)}m`;
        return `${(elapsed / TIME_CONSTANTS.HOUR_MS).toFixed(1)}h`;
    };

    return (
        <div className="space-y-3">
            <div className="relative">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                        className={`h-full transition-all duration-300 ${getProgressColor()}`}
                        style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                    />
                </div>
                <div className="flex justify-between items-center mt-1 text-xs text-gray-600">
                    <span>{progress.toFixed(1)}% Complete</span>
                    {startTime && (
                        <span>Elapsed: {formatElapsedTime()}</span>
                    )}
                </div>
            </div>

            {currentPhase && (
                <div className="flex items-center space-x-2">
                    {isActive && (
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    )}
                    <span className="text-sm font-medium text-gray-700">
                        {currentPhase}
                    </span>
                </div>
            )}

            {details && (
                <div className="bg-gray-50 p-3 rounded text-xs text-gray-600 font-mono">
                    {typeof details === 'string' ? details : JSON.stringify(details, null, 2)}
                </div>
            )}
        </div>
    );
};

export default ProgressIndicator;
